import { Component, type ReactNode, type ErrorInfo } from 'react';
import { AlertCircle } from '../shared/icons';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
  }

  override render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-stone-50 p-6">
          <div className="max-w-md w-full bg-white p-10 rounded-[2.5rem] border border-stone-200 shadow-xl text-center">
            <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-8 h-8" />
            </div>
            <h2 className="text-2xl font-serif mb-4">Something went wrong</h2>
            <p className="text-stone-600 mb-8 text-sm leading-relaxed">
              An unexpected error occurred. We've been notified and are looking into it.
            </p>
            <div className="bg-stone-50 p-4 rounded-2xl mb-8 text-left overflow-auto max-h-32">
              <code className="text-[10px] text-red-600 font-mono">
                {this.state.error?.message === 'Script error.'
                  ? 'Script error: An external script (like PayPal or reCAPTCHA) failed to load correctly. This is often due to browser extensions, ad-blockers, or cross-origin restrictions.'
                  : this.state.error?.message}
              </code>
            </div>
            <button
              onClick={() => window.location.reload()}
              className="w-full bg-stone-900 text-stone-50 py-3 rounded-full font-medium hover:bg-stone-800 transition-all"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
